import { z } from 'zod';

// Common utility schemas
const ZDateString = z.string().describe('Date string in ISO format');
const ZOptionalDateString = z
  .string()
  .nullable()
  .describe('Optional date string in ISO format');

// =====================================
// AUTHENTICATION SCHEMAS
// =====================================

// Authentication request schema
export const ZRusadaAuthInput = z.object({
  username: z.string().min(1).describe('Rusada username'),
  password: z.string().min(1).describe('Rusada password'),
});

// Authentication response schema
export const ZRusadaAuthResponse = z.object({
  token: z.string().describe('Access token for API authentication'),
  refreshToken: z.string().describe('Refresh token for token renewal'),
});

// Refresh token request schema
export const ZRusadaRefreshTokenInput = z.object({
  token: z.string().describe('Current access token'),
  refreshToken: z.string().describe('Refresh token for renewal'),
});

// =====================================
// REGISTRATION SCHEMAS
// =====================================

// Registration response schema (from API)
export const ZRusadaRegistrationResponse = z.object({
  id: z
    .number()
    .int()
    .positive()
    .describe('Unique identifier for the registration (e.g., 34)'),
  assetId: z
    .number()
    .int()
    .positive()
    .describe('Asset identifier linked to the registration'),
  registration: z
    .string()
    .min(1)
    .describe('Aircraft registration number (e.g., ZM300)'),
  statusId: z.number().int().describe('Status identifier for the registration'),
  status: z
    .string()
    .describe('Human-readable status description (e.g., ACTIVE)'),
  serialNo: z.string().describe('Aircraft serial number (e.g., 11099)'),
  operator: z.string().describe('Operator name (e.g., AFF)'),
  isLocked: z
    .boolean()
    .describe('Whether the registration is locked for modifications'),
});

// =====================================
// WORK ORDER SCHEMAS
// =====================================

/**
 * Veryon/Rusada Work Order Status Enum
 * Retrieved from /v1/WorkOrders/Statuses API
 *
 * Status meanings:
 * - FOR_PLANNING: Pre-planning stage, not actionable (isForPlanning=true, isDefaultOpen=true)
 * - PLANNED: Planning complete, scheduled but not started (isPlanned=true, isForPlanning=true)
 * - OPEN_IN_WORK: Active maintenance in progress (isInWork=true)
 * - QUOTED: Quote/estimate stage, not actionable
 * - CLOSED_145: Completed work (specific closure type)
 * - CLOSED_PART_M: Completed work (isDefaultClosed=true)
 * - CANCELLED: Cancelled work, should not sync (isCancelled=true)
 */
export enum RusadaWorkOrderStatus {
  FOR_PLANNING = 'For Planning',
  PLANNED = 'Planned',
  OPEN_IN_WORK = 'Open - In Work',
  QUOTED = 'Quoted',
  CLOSED_145 = 'Closed - 145',
  CLOSED_PART_M = 'Closed - Part M',
  CANCELLED = 'Cancelled',
}

/**
 *
 * Veryon/Rusada Work Order Range Enum
 * Retrieved from /v1/WorkOrders/Ranges API
 *
 * Maintenance-related ranges (should sync to TechLog):
 * - LINE_01, LINE_Z01: Line maintenance
 * - BASE_02, BASE_Z02, BASE_MAINTENANCE_ORDER: Base maintenance
 * - DEFECTS: Defect work orders
 * - SERVICE: Service orders
 * - COMPONENT_REPAIR: Component repair orders
 *
 * Non-maintenance ranges (inventory/procurement, should NOT sync):
 * - PURCHASE, REPAIR, LOAN, EXCHANGE: Inventory workflows
 * - TOOL_*, TRO, MPO: Tool and migration orders
 * - OVERHEAD_FACILITIES, BASE_MAINTENANCE_REPLENISHMENT, TYRE_BAY: Support operations
 *
 * Deprecated aircraft-specific ranges (marked "DO NOT USE"):
 * - ZEMB*, ZGRB*, ZTEX*: Legacy Embraer, Grob, Texan ranges
 *
 * * Note: Even though these are dynamic and can change in Veryon's backend,
 * our mappings will be affected by their changes and we'd
 * rather see failure and fix, and have clarity, than do default mappings and create unwanted behaviour.
 */
export enum RusadaWorkOrderRange {
  // Maintenance ranges (sync to TechLog)
  LINE_01 = '01-Line',
  BASE_02 = '02-Base',
  LINE_Z01 = 'Z01-Line',
  BASE_Z02 = 'Z02-Base',
  BASE_MAINTENANCE_ORDER = 'BMO',
  DEFECTS = 'Defects',
  SERVICE = 'Service',
  COMPONENT_REPAIR = 'CRO',

  // Non-maintenance ranges (do not sync)
  PURCHASE = 'Purchase',
  REPAIR = 'Repair',
  LOAN = 'Loan',
  EXCHANGE = 'Exchange',
  TOOL_PURCHASE = 'Tool PO',
  TOOL_REPAIR = 'TRO',
  MIGRATION_PO = 'MPO',
  OVERHEAD_FACILITIES = '05-OHF',
  BASE_MAINTENANCE_REPLENISHMENT = 'BMR',
  TYRE_BAY = 'TYB',
  AIRCREW_EQUIPMENT = 'AEA',
  ASCENT_MOD = 'ASCENT/MOD',

  // Deprecated aircraft-specific ranges (DO NOT USE)
  ZEMB_BASE_MAINTENANCE = 'ZEMBBMO',
  ZEMB_DEFECT = 'ZEMBDEF',
  ZEMB_SCHEDULED = 'ZEMBSMI',
  ZGRB_BASE_MAINTENANCE = 'ZGRBBMO',
  ZGRB_DEFECT = 'ZGRBDEF',
  ZGRB_SCHEDULED = 'ZGRBSMI',
  ZTEX_BASE_MAINTENANCE = 'ZTEXBMO',
  ZTEX_DEFECT = 'ZTEXDEF',
  ZTEX_SCHEDULED = 'ZTEXSMI',
}

// Work order response schema (from API)
export const ZRusadaWorkOrderResponse = z.object({
  id: z
    .number()
    .int()
    .positive()
    .describe('Unique identifier for the work order'),
  orderNo: z.string().describe('Work order number identifier'),
  description: z.string().describe('Description of the work order'),
  workOrderStatusId: z.number().int().describe('Work order status identifier'),
  workOrderStatus: z.string().describe('Human-readable work order status'),
  workOrderRangeId: z.number().int().describe('Work order range identifier'),
  workOrderRange: z.string().describe('Work order range description'),
  openDate: ZDateString.describe('Date when the work order was opened'),
  plannedStartDate: ZDateString.describe(
    'Planned start date for the work order',
  ),
  actualStartDate: ZOptionalDateString.describe(
    'Actual start date (null if not started)',
  ),
  plannedFinishDate: ZDateString.describe(
    'Planned completion date for the work order',
  ),
  actualFinishDate: ZOptionalDateString.describe(
    'Actual completion date (null if not finished)',
  ),
  closeDate: ZOptionalDateString.describe(
    'Date when the work order was closed (null if still open)',
  ),
  registrationId: z
    .number()
    .int()
    .positive()
    .describe('Registration ID associated with the work order'),
  registration: z.string().describe('Aircraft registration number'),
  assetId: z
    .number()
    .int()
    .positive()
    .describe('Asset ID associated with the work order'),
  serialNo: z.string().describe('Aircraft serial number'),
  modelId: z.number().int().positive().describe('Aircraft model identifier'),
  model: z.string().describe('Aircraft model name'),
  baseId: z.number().int().positive().describe('Base location identifier'),
  base: z.string().describe('Base location name'),
});

// =====================================
// FLIGHT SCHEMAS
// =====================================

// Flight response schema (from API)
export const ZRusadaFlightResponse = z.object({
  id: z.number().int().positive().describe('Unique identifier for the flight'),
  flightStatusId: z.number().int().describe('Flight status identifier'),
  flightStatusDescription: z.string().describe('Human-readable flight status'),
  flightDate: z.date().describe('Date of the flight'),
  departurePlaceId: z
    .number()
    .int()
    .positive()
    .describe('Departure location identifier'),
  departurePlaceDescription: z.string().describe('Departure location name'),
  departureScheduled: z.date().describe('Scheduled departure time'),
  departureEstimate: z.date().describe('Estimated departure time'),
  departureActual: z.date().describe('Actual departure time'),
  departureTakeOff: z.date().describe('Actual takeoff time'),
  arrivalPlaceId: z
    .number()
    .int()
    .positive()
    .describe('Arrival location identifier'),
  arrivalPlaceDescription: z.string().describe('Arrival location name'),
  arrivalScheduled: z.date().describe('Scheduled arrival time'),
  arrivalEstimate: z.date().describe('Estimated arrival time'),
  arrivalLanded: z.date().describe('Actual landing time'),
  arrivalActual: z.date().describe('Actual arrival time'),
  divertedPlaceId: z
    .number()
    .int()
    .describe('Diverted location identifier (0 if no diversion)'),
  divertedPlaceDescription: z.string().describe('Diverted location name'),
  divertedReason: z.string().describe('Reason for diversion'),
  divertedArrivalTime: z.date().describe('Diverted arrival time'),
  customerId: z.number().int().describe('Customer identifier'),
  customerDescription: z.string().describe('Customer name/description'),
  flightModelId: z.number().int().describe('Flight model identifier'),
  flightModelDescription: z.string().describe('Flight model description'),
  flightLineId: z.number().int().describe('Flight line identifier'),
  flightLineDescription: z.string().describe('Flight line description'),
  flightTypeId: z.number().int().describe('Flight type identifier'),
  flightTypeDescription: z.string().describe('Flight type description'),
  flightNumberId: z.number().int().describe('Flight number identifier'),
  flightNumberDescription: z.string().describe('Flight number description'),
  flightRegistrationId: z
    .number()
    .int()
    .positive()
    .describe('Flight registration identifier'),
  flightRegistrationDescription: z
    .string()
    .describe('Aircraft registration for the flight'),
  plannedFlightTime: z.number().describe('Planned flight time in minutes'),
  calculatedTakeOffTime: z.date().describe('Calculated takeoff time'),
  abortCancelCodeId: z.number().int().describe('Abort/cancel code identifier'),
  abortCancelCodeDescription: z
    .string()
    .describe('Abort/cancel code description'),
});

// Flight input schema (for creating/updating flights)
export const ZRusadaFlightInput = z.object({
  ignoreValidations: z
    .boolean()
    .optional()
    .describe('Whether to ignore API validations'),
  flightDate: z.date().describe('Date of the flight'),
  departurePlaceId: z
    .number()
    .int()
    .positive()
    .describe('Departure location identifier'),
  arrivalPlaceId: z
    .number()
    .int()
    .positive()
    .describe('Arrival location identifier'),
  scheduledTimeDeparture: z.date().describe('Scheduled departure time'),
  scheduledTimeArrival: z.date().describe('Scheduled arrival time'),
  flightTypeId: z.number().int().optional().describe('Flight type identifier'),
  modelId: z.number().int().optional().describe('Aircraft model identifier'),
  registrationId: z
    .number()
    .int()
    .positive()
    .describe('Aircraft registration identifier'),
  flightNumber: z.string().min(1).describe('Flight number'),
  customerId: z.number().int().optional().describe('Customer identifier'),
  expectedPassengers: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Expected number of passengers'),
  missionId: z.number().int().optional().describe('Mission identifier'),
});

// =====================================
// DEFECT SCHEMAS
// =====================================

// Defect input schema (for creating/updating defects)
export const ZRusadaDefectInput = z.object({
  registrationId: z
    .number()
    .int()
    .positive()
    .describe('Aircraft registration identifier'),
  defectText: z.string().min(1).describe('Detailed description of the defect'),
  defectCategoryId: z
    .number()
    .int()
    .optional()
    .describe('Defect category identifier'),
  techLogId: z.number().int().optional().describe('Technical log identifier'),
  flightId: z
    .number()
    .int()
    .optional()
    .describe('Associated flight identifier'),
  sequenceNumber: z.string().describe('Defect sequence number'),
  itemNumber: z
    .string()
    .optional()
    .describe('Item number for sync tracking (becomes defectItemNo)'),
  componentId: z.number().int().optional().describe('Component identifier'),
  defectTypeId: z
    .number()
    .int()
    .describe('Defect type: 1=MAREP (maintenance/cabin), 2=PIREP (pilot)'),
  ataChapterId: z.number().int().optional().describe('ATA chapter identifier'),
  baseId: z
    .number()
    .int()
    .optional()
    .describe('Base/report location identifier'),
  isStructuralItem: z
    .boolean()
    .optional()
    .describe('Whether defect affects structural item'),
  excludeFromReliability: z
    .boolean()
    .optional()
    .describe('Whether to exclude from reliability analysis'),
  userId: z
    .number()
    .int()
    .optional()
    .describe('Reporter employee ID in Rusada'),
});

// =====================================
// TECHLOG SCHEMAS
// =====================================

/**
 * Life code input schema for sector-level tracking.
 * Used for explicitly setting life code values on sectors (Affinity has no automatic updates configured).
 */
export const ZRusadaSectorLifeCodeInput = z.object({
  locationId: z
    .number()
    .int()
    .describe(
      'Location identifier (0 for AIRFRAME, 198 for 72-00 engine location)',
    ),
  locationPositionId: z
    .number()
    .int()
    .describe(
      'Position within location (0 for single/airframe, 5 for RH, 6 for LH)',
    ),
  lifeCodeId: z
    .number()
    .int()
    .positive()
    .describe('Life code ID from registration LifeCodes lookup'),
  usage: z.number().int().describe('Usage value for this life code'),
});

// TechLog sector input schema (for creating TechLogs with sectors)
export const ZRusadaTechLogSectorInput = z.object({
  flightTypeId: z.number().int().optional(),
  departurePlaceId: z.number().int().positive(),
  arrivalPlaceId: z.number().int().positive(),
  takeOffTimeUtc: z.string(),
  landingTimeUtc: z.string(),
  flightHours: z.number().int().nonnegative().optional(),
  landings: z.number().int().nonnegative().optional(),
  cycles: z.number().int().nonnegative().optional(),
  lifeCodes: z.array(ZRusadaSectorLifeCodeInput).optional(),
});

// TechLog creation input schema
// techLogCategoryId: 1 = Tech Log (regular flights), maintenance category for trip updates/adjustments
export const ZRusadaTechLogInput = z.object({
  techLogNo: z.string().min(1),
  techLogDateUtc: z.string(),
  registrationId: z.number().int().positive(),
  techLogCategoryId: z.number().int().positive(),
  flightTypeId: z.number().int().optional(),
  sectors: z.array(ZRusadaTechLogSectorInput).min(1),
});

// TechLog response schema
export const ZRusadaTechLogResponse = z.object({
  id: z.number().int().positive().describe('TechLog ID'),
  techLogNo: z.string().describe('TechLog number'),
  techLogDateUtc: z.string().describe('TechLog date in UTC'),
  registrationId: z
    .number()
    .int()
    .positive()
    .describe('Aircraft registration ID'),
  techLogCategoryId: z.number().int().describe('TechLog category ID'),
  flightTypeId: z.number().int().optional().describe('Flight type ID'),
  flightId: z
    .number()
    .int()
    .optional()
    .describe('Associated flight identifier'),
  sequenceNumber: z.string().optional().describe('Defect sequence number'),
  itemNumber: z
    .string()
    .optional()
    .describe('Item number associated with the defect'),
  componentId: z.number().int().optional().describe('Component identifier'),
  defectTypeId: z.number().int().optional().describe('Defect type identifier'),
  ataChapterId: z.number().int().optional().describe('ATA chapter identifier'),
  baseId: z.number().int().optional().describe('Base location identifier'),
  isStructuralItem: z
    .boolean()
    .optional()
    .describe('Whether the defect affects a structural item'),
  excludeFromReliability: z
    .boolean()
    .optional()
    .describe('Whether to exclude from reliability analysis'),
  userId: z
    .number()
    .int()
    .optional()
    .describe('User identifier who reported the defect'),
});

export const ZRusadaDefectResponse = z.object({
  id: z.number().int(),
  defectItemNo: z.string().nullish(),
  defect: z.string().nullish(),
  regId: z.number().int().nullish(),
  reg: z.string().nullish(),
  defectStatusId: z.number().int().nullish(),
  defectStatus: z.string().nullish(),
  reportedByEmployeeId: z.number().int().nullish(),
  reportedByFirstName: z.string().nullish(),
  reportedBySurname: z.string().nullish(),
  carriedOutByEmployeeId: z.number().int().nullish(),
  carriedOutByFirstName: z.string().nullish(),
  carriedOutBySurname: z.string().nullish(),
  techLogId: z.number().int().nullish(),
  techLogNo: z.string().nullish(),
  flightId: z.number().int().nullish(),
  flightNumber: z.string().nullish(),
  ataChapterId: z.number().int().nullish(),
  ataChapter: z.string().nullish(),
  defectTypeId: z.number().int().nullish(),
  defectType: z.string().nullish(),
  defectCategoryId: z.number().int().nullish(),
  defectCategory: z.string().nullish(),
  addReferenceId: z.number().int().nullish(),
  addReference: z.string().nullish(),
  melReference: z.string().nullish(),
  melPriorityId: z.number().int().nullish(),
  melPriority: z.string().nullish(),
  melCategory: z.string().nullish(),
  targetDate: z.string().nullish(),
  defectSequenceNo: z.string().nullish(),
  actionText: z.string().nullish(),
  openDate: z.string().nullish(),
  closeDate: z.string().nullish(),
  isReliability: z.boolean().nullish(),
  remainingDays: z.number().int().nullish(),
  lastModifed: z.string().nullish(),
});

/**
 * Defect close input schema
 * Per API deep dive: PUT /v1/Defects/{defectId}/Close
 * Used for closing, cancelling, or deleting defects (all terminal states use Close endpoint)
 */
export const ZRusadaDefectCloseInput = z.object({
  defectId: z
    .number()
    .int()
    .positive()
    .describe('Defect ID (required in body AND URL path)'),
  carriedOutDateTimeUtc: z
    .string()
    .describe(
      'Closure date/time in ISO format (REQUIRED, must be after defect creation, not in future)',
    ),
  techLogSectorId: z
    .number()
    .int()
    .positive()
    .describe('TechLog sector ID (REQUIRED at runtime)'),
  carriedOutByEmployeeId: z
    .number()
    .int()
    .positive()
    .describe('Employee ID who performed the closure (REQUIRED at runtime)'),
  actionText: z
    .string()
    .optional()
    .describe(
      'Resolution description (recommended, use "CANCELLED: reason" or "DELETED: reason" for non-closure terminal states)',
    ),
  timeSpentMinutes: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Labor time in minutes'),
  faultCode: z.string().optional().describe('Fault code reference'),
});

/**
 * Defect defer input schema
 * Per API deep dive: PUT /v1/Defects/{defectId}/Defer
 * Only defects in "Open" status (4) can be deferred. Already-deferred defects return error 457.
 */
export const ZRusadaDefectDeferInput = z.object({
  defectId: z
    .number()
    .int()
    .positive()
    .describe('Defect ID (required in body AND URL path)'),
  actionText: z.string().min(1).describe('Deferral reason/notes (REQUIRED)'),
  addReference: z
    .string()
    .max(20)
    .describe('ADD reference (REQUIRED, max 20 characters)'),
  techLogSectorId: z
    .number()
    .int()
    .positive()
    .describe('TechLog sector ID (REQUIRED)'),
  melCategoryId: z
    .number()
    .int()
    .min(1)
    .max(6)
    .describe(
      'MEL category ID: 1=MEL_A, 2=MEL_B, 3=MEL_C, 4=MEL_D, 5=NO_MEL, 6=HUSB',
    ),
  targetDateUtc: z
    .string()
    .describe(
      'Target date/time in ISO format (REQUIRED, even with life limits)',
    ),
  melReference: z
    .string()
    .describe('MEL reference (REQUIRED but can be empty string "")'),
  timeSpentMinutes: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Labor time in minutes'),
  deferralReasonId: z
    .number()
    .int()
    .optional()
    .describe('Deferral reason lookup ID'),
  targetLifeMinutes: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Life-based limit in minutes (alternative to date)'),
  targetLifeCycles: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Cycle-based limit (alternative to date)'),
  carriedOutDateTimeUtc: z
    .string()
    .optional()
    .describe('Date/time when deferral was carried out (ISO format)'),
  carriedOutByEmployeeId: z
    .number()
    .int()
    .positive()
    .optional()
    .describe('Employee ID who carried out the deferral'),
  carriedOutByStampId: z
    .number()
    .int()
    .positive()
    .optional()
    .describe('Stamp ID for the deferral action'),
});

export type RusadaDefectDeferInput = z.infer<typeof ZRusadaDefectDeferInput>;

// Defect status schema
export const ZRusadaDefectStatus = z.object({
  id: z.number().int().describe('Status ID'),
  status: z.string().describe('Status name'),
  description: z.string().describe('Status description'),
  defaultOpen: z.boolean().describe('Whether this is the default open status'),
  onOrder: z.boolean().describe('Whether this status indicates on order'),
  defaultClosed: z
    .boolean()
    .describe('Whether this is the default closed status'),
  defaultStatus: z.boolean().describe('Whether this is the default status'),
  deferred: z.boolean().describe('Whether this status indicates deferred'),
  continue: z.boolean().describe('Whether this status indicates continue'),
  transferred: z
    .boolean()
    .describe('Whether this status indicates transferred'),
  colour: z.number().nullable().describe('Status color code'),
});

// Defect category schema
export const ZRusadaDefectCategory = z.object({
  id: z.number().int().describe('Category ID'),
  description: z.string().describe('Category description'),
  category: z.string().describe('Category name'),
  isComponentDefect: z
    .boolean()
    .describe('Whether this category is for component defects'),
});

// Employee schema (from GET /v1/Employees)
export const ZRusadaEmployee = z.object({
  id: z.number().int().positive().describe('Employee ID'),
  firstName: z.string().describe('Employee first name'),
  surname: z.string().describe('Employee surname'),
  employeeUsername: z.string().describe('Employee username (email)'),
});

// MEL category schema (from GET /v1/Defects/MELCategories)
export const ZRusadaMelCategoryResponse = z.object({
  id: z
    .number()
    .int()
    .describe('MEL category ID (matches melPriorityId on defects)'),
  melPriority: z
    .string()
    .describe('MEL priority label (e.g., "MEL A", "NO MEL", "HUSB")'),
  description: z.string().describe('MEL category description'),
  days: z
    .number()
    .int()
    .describe('Default calendar days for this MEL category'),
  noTargetDate: z
    .boolean()
    .describe('Whether this category has no target date'),
});

export const ZRusadaMelCategoriesResponse = z.array(ZRusadaMelCategoryResponse);

// Array schemas for the endpoints
export const ZRusadaDefectStatusesResponse = z.array(ZRusadaDefectStatus);
export const ZRusadaDefectCategoriesResponse = z.array(ZRusadaDefectCategory);

// =====================================
// LIFE CODES SCHEMAS
// =====================================

// Life code response schema (from /v1/LifeCodes - global lookup)
export const ZRusadaLifeCodeResponse = z.object({
  id: z
    .number()
    .int()
    .positive()
    .describe('Unique identifier for the life code'),
  lifeCode: z
    .string()
    .describe('Life code name (e.g., "FLIGHT HOURS", "Fuel")'),
  lifeCodeDescription: z
    .string()
    .describe('Human-readable description of the life code'),
  displayCharacter: z
    .string()
    .describe('Short display character/abbreviation (e.g., "FH", "ltrs")'),
});

// Array of life codes
export const ZRusadaLifeCodesResponse = z.array(ZRusadaLifeCodeResponse);

// Registration-specific life code response (from /v1/Registrations/{registrationId}/LifeCodes)
// Returns aircraft-specific lifecodes with location/position info for airframe and engine tracking
export const ZRusadaRegistrationLifeCodeResponse = z.object({
  locationId: z
    .number()
    .int()
    .describe('Location ID (0 = AIRFRAME, 198 = 72-00 engine location)'),
  location: z.string().describe('Location name (e.g., "AIRFRAME", "72-00")'),
  locationPositionId: z
    .number()
    .int()
    .describe('Position ID within location (0 = none, 5 = RH, 6 = LH)'),
  locationPosition: z
    .string()
    .describe('Position name (empty string, "RH", or "LH")'),
  partId: z.number().int().describe('Part identifier'),
  partNo: z.string().describe('Part number'),
  assetId: z.number().int().describe('Asset identifier'),
  serialNo: z.string().describe('Serial number'),
  lifeCodeId: z.number().int().describe('Life code ID'),
  lifeCode: z
    .string()
    .describe(
      'Life code name (e.g., "FLIGHT HOURS", "TOTAL LANDINGS", "ENGINE CYCLES")',
    ),
  lifeTotal: z.number().describe('Current total value for this life code'),
});

export const ZRusadaRegistrationLifeCodesResponse = z.array(
  ZRusadaRegistrationLifeCodeResponse,
);

// =====================================
// PLACES SCHEMAS
// =====================================

// Places response schema (from API)
export const ZRusadaPlacesResponse = z.object({
  id: z.number().int().positive().describe('Unique identifier for the place'),
  place: z.string().describe('Place code or identifier'),
  placeName: z.string().describe('Full name of the place'),
  placeRemarks: z.string().describe('Additional remarks about the place'),
  timeZoneId: z.number().int().nullable().describe('Time zone identifier'),
  iataCode: z.string().describe('IATA airport code'),
  icaoCode: z.string().describe('ICAO airport code'),
  lHotelIdDefault: z
    .number()
    .int()
    .nullable()
    .describe('Default layover hotel identifier'),
  oHotelIdDefault: z
    .number()
    .int()
    .nullable()
    .describe('Default overnight hotel identifier'),
  colour: z.number().int().nullable().describe('Color code for the place'),
});

// =====================================
// SCHEDULED MAINTENANCE SCHEMAS
// =====================================

/**
 * Scheduled maintenance threshold types from Rusada.
 * Retrieved from GET /v1/Registrations/{registrationId}/ScheduledMaintenance API.
 *
 * NOTE: This is DIFFERENT from Registration Life Codes (ZRusadaLifeCodeResponse).
 * - Registration Life Codes: Tracked values per aircraft/component (hours, cycles on airframe)
 * - Scheduled MX Threshold Types: Category indicating how to parse the maintenance item's `remaining` field
 *
 * Threshold mapping for Affinity (SOW):
 * - FLIGHT_HOURS: 150 FH threshold
 * - DAYS / BY_DATE / MONTHS: 50 Days threshold (MONTHS converted to days)
 * - ENGINE_CYCLES / ENGINE_STARTS: 50 EC threshold
 * - TOTAL_LANDINGS: 100 FL threshold
 * - ENGINE_HOURS: 150 (same as Flight Hours)
 */
export enum RusadaScheduledMxThresholdType {
  FLIGHT_HOURS = 'FLIGHT HOURS',
  ENGINE_HOURS = 'ENGINE HOURS',
  DAYS = 'DAYS',
  MONTHS = 'MONTHS',
  BY_DATE = 'BY DATE',
  TOTAL_LANDINGS = 'TOTAL LANDINGS',
  ENGINE_CYCLES = 'ENGINE CYCLES',
  ENGINE_STARTS = 'Engine Starts',
}

/**
 * Scheduled Maintenance response schema
 * From GET /v1/Registrations/{registrationId}/ScheduledMaintenance
 *
 * Key fields for threshold filtering:
 * - lifeCode: Determines unit type and threshold to apply
 * - remaining: Parsed value determines if within sync threshold
 * - lastModified: Used for client-side delta sync filtering
 */
export const ZRusadaScheduledMaintenanceResponse = z.object({
  id: z
    .number()
    .int()
    .positive()
    .describe('Unique identifier for the scheduled maintenance item'),
  mi: z.string().describe('Maintenance item code (e.g., "PHE-29-0003")'),
  lifeCode: z
    .string()
    .describe(
      'Threshold type: FLIGHT HOURS, DAYS, MONTHS, BY DATE, TOTAL LANDINGS, ENGINE CYCLES, ENGINE HOURS, Engine Starts',
    ),
  currentLife: z
    .string()
    .describe('Current aircraft value for this life code (e.g., "3370:55 FH")'),
  remaining: z
    .string()
    .describe(
      'Remaining until due - KEY FIELD for threshold filtering (e.g., "42:45 FH", "-27DY", "259FL")',
    ),
  life: z.string().describe('Interval value (e.g., "1600:00 FH")'),
  nextDue: z.string().describe('Next due threshold value (e.g., "3413:40 FH")'),
  dueDate: z.string().nullable().describe('Calculated due date in ISO format'),
  workOrderId: z
    .number()
    .int()
    .nullable()
    .describe('Associated work order ID (null if not in a work order)'),
  workOrderNo: z.string().describe('Work order number (empty string if none)'),
  title: z.string().describe('Maintenance description/title'),
  componentAssetId: z
    .number()
    .int()
    .nullable()
    .describe('Component asset reference'),
  lastModified: z
    .string()
    .nullable()
    .describe('Last modification timestamp for delta sync (ISO format)'),
});

export const ZRusadaScheduledMaintenanceArrayResponse = z.array(
  ZRusadaScheduledMaintenanceResponse,
);

// =====================================
// API REQUEST PARAMETER SCHEMAS
// =====================================

// Fetch work orders parameters
export const ZRusadaFetchWorkOrdersInput = z.object({
  registration: z
    .string()
    .optional()
    .describe('Filter by aircraft registration'),
});

// Fetch flights parameters
export const ZRusadaFetchFlightsInput = z.object({
  dateFrom: z.date().optional().describe('Filter flights from this date'),
  dateTo: z.date().optional().describe('Filter flights to this date'),
});

// Fetch places parameters
export const ZRusadaFetchPlacesInput = z.object({
  icaoCode: z.string().optional().describe('Filter by ICAO code'),
});

// =====================================
// ERROR RESPONSE SCHEMAS
// =====================================

// Generic error response from Rusada API
export const ZRusadaErrorResponse = z.object({
  error: z.string().describe('Error message'),
  errorDescription: z
    .string()
    .optional()
    .describe('Detailed error description'),
  statusCode: z.number().int().optional().describe('HTTP status code'),
});

// =====================================
// COMBINED SCHEMAS FOR ARRAY RESPONSES
// =====================================

// =====================================
// ATA CHAPTER SCHEMAS
// =====================================

// ATA Chapter response schema (from GET /v1/Ata/Chapters)
export const ZRusadaAtaChapterResponse = z.object({
  id: z
    .number()
    .int()
    .describe(
      'ATA chapter identifier in Rusada system (e.g., 86 for ATA PENDING)',
    ),
  description: z
    .string()
    .describe(
      'ATA chapter description (e.g., "ATA PENDING", "AIR CONDITIONING")',
    ),
  ata: z
    .string()
    .describe(
      'Full ATA code with chapter-system format (e.g., "00-00", "21-00")',
    ),
  chapter: z.string().describe('ATA chapter code (e.g., "00", "02", "21")'),
  system: z.string().describe('ATA system code within chapter (e.g., "00")'),
});

export const ZRusadaAtaChaptersResponse = z.array(ZRusadaAtaChapterResponse);

// =====================================
// TECHLOG CATEGORY SCHEMAS
// =====================================

export const ZRusadaTechLogCategoryResponse = z.object({
  id: z.number().int(),
  category: z.string(),
  isTechLog: z.boolean(),
  isMaintenanceEntry: z.boolean(),
  isCabinLog: z.boolean(),
});

export const ZRusadaTechLogCategoriesResponse = z.array(
  ZRusadaTechLogCategoryResponse,
);

// =====================================
// FLIGHT TYPE SCHEMAS
// =====================================

export const ZRusadaFlightTypeResponse = z.object({
  id: z.number().int(),
  type: z.string(),
  description: z.string(),
  positioningFlight: z.boolean(),
  trainingFlight: z.boolean(),
  newJourneyDefault: z.boolean(),
});

export const ZRusadaFlightTypesResponse = z.array(ZRusadaFlightTypeResponse);

// =====================================
// OIL AND FUEL SCHEMAS
// =====================================

export const ZRusadaOilAndFuelInput = z.object({
  flightId: z
    .number()
    .int()
    .describe(
      'Sector ID from GET /v1/TechLogs/{techLogId}/Sectors - used as flightId for this endpoint',
    ),
  upliftUnitOfMeasureId: z
    .number()
    .int()
    .nullable()
    .describe(
      'Unit of measure ID for uplifts - nullable (sandbox has no units configured, accepts null)',
    ),
  fuelCarriedFromLastJourney: z
    .number()
    .describe('Fuel remaining from previous journey'),
  fuelUpliftPlanned: z.number().describe('Planned fuel uplift amount'),
  fuelUpliftActual: z.number().describe('Actual fuel uplift amount'),
  departureFuel: z.number().describe('Total fuel at departure'),
  arrivalFuel: z.number().describe('Total fuel at arrival'),
  oilUplift: z.number().describe('Oil uplift amount'),
});

export const ZRusadaOilAndFuelResponse = ZRusadaOilAndFuelInput;

/**
 * A single Additional Oil & Fuel row (vendor `Flights.AdditionalOilAndFuelInfo`).
 * Rows are configured per aircraft model in Tracking+ and keyed by
 * location/position/uplift type/input type. `id: 0` means no usage is stored yet,
 * so the row must be created with POST before later updates go through PUT.
 */
export const ZRusadaAdditionalOilAndFuel = z.object({
  id: z
    .number()
    .int()
    .describe('0 until a usage is stored, then the row identifier for PUT'),
  flightId: z.number().int(),
  regJourneyOilAndFuelId: z.number().int(),
  locationId: z.number().int(),
  locationPositionId: z.number().int(),
  modelAttachedModelLocationId: z.number().int(),
  regJourneyUpliftTypeId: z.number().int(),
  regJourneyUpliftInputId: z.number().int(),
  location: z
    .string()
    .describe('ATA chapter of the fluid location, e.g. 72-00'),
  position: z.string().describe('LH, RH, or empty for a single position'),
  upliftType: z
    .string()
    .describe('Operator-configured label, e.g. "Engine Oil"'),
  inputName: z.string().describe('Input type, e.g. "Before Departure"'),
  upliftUsage: z
    .number()
    .describe('The recorded amount in upliftUnitOfMeasure'),
  journeyNumber: z.string(),
  techLogId: z.number().int(),
  journeyCode: z.string(),
  upliftUnitOfMeasureId: z.number().int(),
  upliftUnitOfMeasure: z
    .string()
    .describe('Configured unit name, e.g. "Oil Litres" or "Oil Quarts"'),
  maxLimit: z.number().describe('0 when no limit is configured'),
  logBookId: z.number().int().nullable(),
  assetId: z.number().int().nullable(),
  modelUpliftInputMappingId: z.number().int().nullable(),
});

export const ZRusadaAdditionalOilAndFuelsResponse = z.array(
  ZRusadaAdditionalOilAndFuel,
);

/**
 * Contract for updating every Additional Oil & Fuel row of a flight in one call.
 * Tracking+ has no bulk endpoint, so the client sends one request per row.
 */
export const ZRusadaAdditionalOilAndFuelInput = z.object({
  flightId: z
    .number()
    .int()
    .describe(
      'Sector ID from GET /v1/TechLogs/{techLogId}/Sectors - used as flightId for this endpoint',
    ),
  uplifts: z
    .array(ZRusadaAdditionalOilAndFuel)
    .describe('Rows read from the flight, each carrying its new upliftUsage'),
});

// Array response schemas
export const ZRusadaRegistrationsResponse = z
  .array(ZRusadaRegistrationResponse)
  .describe('Array of aircraft registrations');
export const ZRusadaWorkOrdersResponse = z
  .array(ZRusadaWorkOrderResponse)
  .describe('Array of work orders');
export const ZRusadaFlightsResponse = z
  .array(ZRusadaFlightResponse)
  .describe('Array of flights');
export const ZRusadaPlacesArrayResponse = z
  .array(ZRusadaPlacesResponse)
  .describe('Array of places');

// =====================================
// EXPORT TYPE INFERENCE
// =====================================

// Export inferred TypeScript types for use in the codebase
export type RusadaAuthInput = z.infer<typeof ZRusadaAuthInput>;
export type RusadaAuthResponse = z.infer<typeof ZRusadaAuthResponse>;
export type RusadaRefreshTokenInput = z.infer<typeof ZRusadaRefreshTokenInput>;
export type RusadaRegistrationResponse = z.infer<
  typeof ZRusadaRegistrationResponse
>;
export type RusadaWorkOrderResponse = z.infer<typeof ZRusadaWorkOrderResponse>;
export type RusadaFlightResponse = z.infer<typeof ZRusadaFlightResponse>;
export type RusadaFlightInput = z.infer<typeof ZRusadaFlightInput>;
export type RusadaDefectInput = z.infer<typeof ZRusadaDefectInput>;
export type RusadaDefectResponse = z.infer<typeof ZRusadaDefectResponse>;
export type RusadaDefectStatus = z.infer<typeof ZRusadaDefectStatus>;
export type RusadaDefectCategory = z.infer<typeof ZRusadaDefectCategory>;
export type RusadaEmployee = z.infer<typeof ZRusadaEmployee>;
export type RusadaDefectStatusesResponse = z.infer<
  typeof ZRusadaDefectStatusesResponse
>;
export type RusadaDefectCategoriesResponse = z.infer<
  typeof ZRusadaDefectCategoriesResponse
>;
export type RusadaMelCategoryResponse = z.infer<
  typeof ZRusadaMelCategoryResponse
>;
export type RusadaMelCategoriesResponse = z.infer<
  typeof ZRusadaMelCategoriesResponse
>;
export type RusadaPlacesResponse = z.infer<typeof ZRusadaPlacesResponse>;
export type RusadaFetchWorkOrdersInput = z.infer<
  typeof ZRusadaFetchWorkOrdersInput
>;
export type RusadaFetchFlightsInput = z.infer<typeof ZRusadaFetchFlightsInput>;
export type RusadaFetchPlacesInput = z.infer<typeof ZRusadaFetchPlacesInput>;
export type RusadaErrorResponse = z.infer<typeof ZRusadaErrorResponse>;
export type RusadaLifeCodeResponse = z.infer<typeof ZRusadaLifeCodeResponse>;
export type RusadaLifeCodesResponse = z.infer<typeof ZRusadaLifeCodesResponse>;
export type RusadaRegistrationLifeCodeResponse = z.infer<
  typeof ZRusadaRegistrationLifeCodeResponse
>;
export type RusadaRegistrationLifeCodesResponse = z.infer<
  typeof ZRusadaRegistrationLifeCodesResponse
>;
export type RusadaSectorLifeCodeInput = z.infer<
  typeof ZRusadaSectorLifeCodeInput
>;
export type RusadaDefectCloseInput = z.infer<typeof ZRusadaDefectCloseInput>;
export type RusadaFlightTypeResponse = z.infer<
  typeof ZRusadaFlightTypeResponse
>;
export type RusadaFlightTypesResponse = z.infer<
  typeof ZRusadaFlightTypesResponse
>;
export type RusadaOilAndFuelInput = z.infer<typeof ZRusadaOilAndFuelInput>;
export type RusadaOilAndFuelResponse = z.infer<
  typeof ZRusadaOilAndFuelResponse
>;
export type RusadaAdditionalOilAndFuel = z.infer<
  typeof ZRusadaAdditionalOilAndFuel
>;
export type RusadaAdditionalOilAndFuelInput = z.infer<
  typeof ZRusadaAdditionalOilAndFuelInput
>;
export type RusadaAtaChapterResponse = z.infer<
  typeof ZRusadaAtaChapterResponse
>;
export type RusadaAtaChaptersResponse = z.infer<
  typeof ZRusadaAtaChaptersResponse
>;
export type RusadaTechLogCategoryResponse = z.infer<
  typeof ZRusadaTechLogCategoryResponse
>;
export type RusadaTechLogCategoriesResponse = z.infer<
  typeof ZRusadaTechLogCategoriesResponse
>;
export type RusadaScheduledMaintenanceResponse = z.infer<
  typeof ZRusadaScheduledMaintenanceResponse
>;
export type RusadaScheduledMaintenanceArrayResponse = z.infer<
  typeof ZRusadaScheduledMaintenanceArrayResponse
>;
